import { type FC, type ReactNode, startTransition, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { Button, type PopupPlacement } from '@direct-frontend/components';
import { FEATURE_HINT_TOOLTIP_TRANSITION_MS } from '../../lib/featureHints';
import { Tooltip, type TooltipProps } from '../../ui/Tooltip';
import styles from './FeatureHintTooltip.module.css';

export type FeatureHintTooltipProps = {
    children: ReactNode;
    isOpen: boolean;
    /** Отличает закрытие пользователем с анимацией от немедленного скрытия перед другой поверхностью. */
    isClosing: boolean;
    title: ReactNode;
    caption?: ReactNode;
    placement: PopupPlacement;
    offset?: TooltipProps['offset'];
    flip?: TooltipProps['flip'];
    onDismiss: () => void;
    /** Сообщает владельцу, что анимация закрытия закончилась и общий слот можно передать следующему показу. */
    onCloseComplete?: () => void;
    onConfirm?: () => void;
    /** Заменяет стандартную кнопку «Понятно»; закрытие обрабатывает владелец действия. */
    buttons?: ReactNode;
    color?: TooltipProps['color'];
    maxWidth?: TooltipProps['maxWidth'];
    isDismissable?: TooltipProps['isDismissable'];
    'data-testid'?: string;
};

/**
 * Онбординг-тултип с кнопкой «Понятно» и сохранением состояния снаружи (persistentStorage).
 * Появление и скрытие: opacity ease-in-out {@link FEATURE_HINT_TOOLTIP_TRANSITION_MS} мс.
 * @kind component
 */
export const FeatureHintTooltip: FC<FeatureHintTooltipProps> = ({
    children,
    isOpen,
    isClosing,
    title,
    caption,
    placement,
    offset,
    flip,
    onDismiss,
    onCloseComplete,
    onConfirm,
    buttons,
    color = 'info',
    maxWidth = '360',
    isDismissable,
    'data-testid': dataTestId,
}) => {
    const triggerRef = useRef<HTMLSpanElement>(null);
    // Актуальная функция хранится отдельно, чтобы обновление родителя не перезапускало уже идущую анимацию.
    const onCloseCompleteRef = useRef(onCloseComplete);
    const closingRef = useRef(false);

    // Базовый Tooltip при isOpen=false сразу размонтируется, поэтому сохраняем его на время анимации закрытия.
    const [tooltipMounted, setTooltipMounted] = useState(isOpen);
    const [enterVisible, setEnterVisible] = useState(false);
    const shouldRenderTooltip = tooltipMounted && (isOpen || isClosing);

    useEffect(() => {
        onCloseCompleteRef.current = onCloseComplete;
    }, [onCloseComplete]);

    useEffect(
        () => () => {
            // Если целевой элемент исчез во время закрытия, анимации больше нет и очередь можно освободить сразу.
            if (closingRef.current) {
                closingRef.current = false;
                onCloseCompleteRef.current?.();
            }
        },
        []
    );

    useEffect(() => {
        if (isOpen) {
            closingRef.current = false;
            startTransition(() => {
                setTooltipMounted(true);
            });

            return undefined;
        }

        // Если поверх страницы открывается Alty или другая панель, подсказку скрываем сразу:
        // анимация оставила бы оба элемента видимыми одновременно.
        if (!isClosing) {
            closingRef.current = false;
            startTransition(() => {
                setTooltipMounted(false);
            });

            return undefined;
        }

        // Закрытое начальное состояние не является завершением анимации и не должно освобождать чужой слот.
        if (!tooltipMounted) {
            return undefined;
        }

        closingRef.current = true;

        const id = window.setTimeout(() => {
            closingRef.current = false;
            startTransition(() => {
                setTooltipMounted(false);
            });
            onCloseCompleteRef.current?.();
        }, FEATURE_HINT_TOOLTIP_TRANSITION_MS);

        return () => window.clearTimeout(id);
    }, [isClosing, isOpen, tooltipMounted]);

    useEffect(() => {
        if (!shouldRenderTooltip || !isOpen) {
            startTransition(() => {
                setEnterVisible(false);
            });

            return;
        }

        let innerFrame = 0;

        const outerFrame = window.requestAnimationFrame(() => {
            innerFrame = window.requestAnimationFrame(() => {
                startTransition(() => {
                    setEnterVisible(true);
                });
            });
        });

        return () => {
            window.cancelAnimationFrame(outerFrame);

            if (innerFrame) {
                window.cancelAnimationFrame(innerFrame);
            }
        };
    }, [shouldRenderTooltip, isOpen]);

    const popupClassName = cn(styles.popupEase, isOpen && enterVisible && styles.popupEaseVisible);

    return (
        <>
            <span className={styles.anchor} ref={triggerRef}>
                {children}
            </span>
            <Tooltip
                buttons={
                    buttons ?? (
                        <Button
                            color="contrast"
                            size="s"
                            onClick={() => {
                                onConfirm?.();
                                onDismiss();
                            }}
                        >
                            Понятно
                        </Button>
                    )
                }
                caption={caption}
                className={popupClassName}
                data-testid={dataTestId}
                hasClose
                isOpen={shouldRenderTooltip}
                placement={placement}
                offset={offset}
                flip={flip}
                title={title}
                triggerRef={triggerRef}
                onClose={onDismiss}
                maxWidth={maxWidth}
                color={color}
                isDismissable={isDismissable}
            />
        </>
    );
};
